import React, { useMemo, useState } from "react";
import { Avatar, EmployeeAvatar, IconBtn, Icon, Input } from "../../../design-system";

/**
 * Picker de empleado con buscador. Cuando no hay selección muestra un campo
 * de búsqueda + lista filtrable. Cuando hay selección muestra la "tarjeta"
 * del empleado con Avatar / nombre / cargo y un botón de cambiar.
 *
 * Nota: el campo "team/grupo" todavía no existe en el modelo Employee; cuando
 * el módulo de Equipos aterrice basta con leer `employee.team_name` aquí.
 *
 * @param {{
 *  employees: Array<{ id: string, first_name?: string, last_name?: string, position?: string, email?: string, avatar_path?: string|null }>,
 *  value: string,
 *  onChange: (id: string) => void,
 *  placeholder?: string,
 *  disabled?: boolean,
 * }} props
 */
export function EmployeePicker({ employees = [], value, onChange, placeholder = "Busca por nombre, cargo o email…", disabled = false }) {
  const [q, setQ] = useState("");

  const selected = useMemo(
    () => (value ? employees.find((e) => e.id === value) : null),
    [value, employees],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((e) => {
      const hay = [
        `${e.first_name || ""} ${e.last_name || ""}`,
        e.position,
        e.email,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [employees, q]);

  if (selected) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          border: "1px solid var(--kr-border)",
          borderRadius: 8,
          background: "var(--kr-bg-subtle)",
        }}
      >
        <EmployeeAvatar
          employeeId={selected.id}
          hasAvatar={!!selected.avatar_path}
          name={fullName(selected)}
          size={40}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--kr-fg-strong)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fullName(selected) || "(sin nombre)"}
          </div>
          {selected.position && (
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 1 }}>
              {selected.position}
            </div>
          )}
          {selected.email && (
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 1 }}>
              {selected.email}
            </div>
          )}
        </div>
        {!disabled && (
          <IconBtn
            icon="X"
            aria-label="Cambiar empleado"
            onClick={() => onChange?.("")}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        border: "1px solid var(--kr-border)",
        borderRadius: 8,
        padding: 10,
        background: "#fff",
      }}
    >
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        icon="Search"
        disabled={disabled}
      />
      <div
        style={{
          maxHeight: 240,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {filtered.length === 0 ? (
          <div
            style={{
              padding: "16px 8px",
              fontSize: 12,
              color: "var(--kr-fg-muted)",
              textAlign: "center",
            }}
          >
            <Icon name="UserX" size={16} style={{ display: "block", margin: "0 auto 6px", opacity: 0.6 }} />
            Sin coincidencias
          </div>
        ) : (
          filtered.map((emp) => (
            <button
              key={emp.id}
              type="button"
              onClick={() => onChange?.(emp.id)}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                cursor: disabled ? "default" : "pointer",
                textAlign: "left",
                width: "100%",
              }}
              onMouseEnter={(e) => {
                if (!disabled) e.currentTarget.style.background = "var(--kr-bg-subtle)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <EmployeeAvatar
                employeeId={emp.id}
                hasAvatar={!!emp.avatar_path}
                name={fullName(emp) || "?"}
                size={32}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--kr-fg-strong)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {fullName(emp) || "(sin nombre)"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--kr-fg-muted)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {emp.position || emp.email || "—"}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function fullName(e) {
  return `${e.first_name || ""} ${e.last_name || ""}`.trim();
}

export default EmployeePicker;
